import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaHeart, FaSave, FaSpinner, FaCheckCircle, FaLightbulb,
  FaUser, FaGraduationCap, FaMapMarkerAlt, FaBriefcase, FaPray,
  FaRulerVertical, FaMoneyBillWave
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// Section wrapper
const Section = ({ icon: Icon, title, children }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
    <div className="bg-gradient-to-r from-[#8B0000] to-[#DC143C] px-5 py-3 flex items-center gap-2">
      <Icon className="text-[#FFD700]" />
      <h3 className="text-white font-semibold font-['Playfair_Display'] text-sm">{title}</h3>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

// Range Input
const RangeInput = ({ label, minValue, maxValue, onMinChange, onMaxChange, minPlaceholder, maxPlaceholder, type = 'text' }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2 font-['Noto_Sans_Devanagari']">{label}</label>
    <div className="flex items-center gap-3">
      <input
        type={type}
        placeholder={minPlaceholder || 'Min'}
        value={minValue || ''}
        onChange={e => onMinChange(e.target.value)}
        className="flex-1 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#DC143C] focus:border-transparent outline-none"
      />
      <span className="text-gray-400 font-medium">ते</span>
      <input
        type={type}
        placeholder={maxPlaceholder || 'Max'}
        value={maxValue || ''}
        onChange={e => onMaxChange(e.target.value)}
        className="flex-1 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#DC143C] focus:border-transparent outline-none"
      />
    </div>
  </div>
);

// Checkbox Group
const CheckboxGroup = ({ label, options, selected = [], onChange }) => {
  const selectedArr = Array.isArray(selected) ? selected : (selected ? [selected] : []);

  const handleToggle = (value) => {
    const updated = selectedArr.includes(value)
      ? selectedArr.filter(v => v !== value)
      : [...selectedArr, value];
    onChange(updated);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2 font-['Noto_Sans_Devanagari']">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map(opt => {
          const isChecked = selectedArr.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => handleToggle(opt)}
              className={`px-3 py-1.5 text-sm rounded-lg border transition-all font-['Noto_Sans_Devanagari'] ${
                isChecked
                  ? 'bg-[#8B0000] text-white border-[#8B0000] shadow-sm'
                  : 'border-gray-300 text-gray-600 hover:border-[#DC143C] hover:text-[#DC143C]'
              }`}
            >
              {isChecked && <FaCheckCircle className="inline mr-1 text-xs" />}
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Select Field
const SelectField = ({ label, value, options, onChange, placeholder }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2 font-['Noto_Sans_Devanagari']">{label}</label>
    <select
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#DC143C] focus:border-transparent outline-none"
    >
      <option value="">{placeholder || 'कोणतेही'}</option>
      {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
    </select>
  </div>
);

// Text Input
const TextField = ({ label, value, onChange, placeholder }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2 font-['Noto_Sans_Devanagari']">{label}</label>
    <input
      type="text"
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#DC143C] focus:border-transparent outline-none"
    />
  </div>
);

const PartnerPreferencePage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    ageMin: '', ageMax: '',
    heightMin: '', heightMax: '',
    incomeMin: '', incomeMax: '',
    religion: [],
    caste: [],
    maritalStatus: '',
    motherTongue: [],
    qualification: [],
    occupation: [],
    city: [],
    state: [],
    complexion: [],
    diet: '',
    smoking: '',
    drinking: '',
  });

  // Smart suggestions based on user profile
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/profiles/preferences');
      const data = response.data.preferences || response.data;
      if (data && typeof data === 'object') {
        setPreferences(prev => ({ ...prev, ...data }));
      }
      generateSuggestions();
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
      generateSuggestions();
    } finally {
      setLoading(false);
    }
  };

  const generateSuggestions = () => {
    const tips = [];
    if (user?.age) {
      const suggestedMin = user.gender === 'female' ? user.age : user.age - 5;
      const suggestedMax = user.gender === 'female' ? user.age + 5 : user.age;
      tips.push(`तुमच्या वयानुसार ${suggestedMin}-${suggestedMax} वर्षे वय श्रेणी सुचवली जाते`);
    }
    if (user?.religion) {
      tips.push(`तुमचा धर्म "${user.religion}" आहे, तो निवडला जाऊ शकतो`);
    }
    if (user?.city) {
      tips.push(`तुमचे शहर "${user.city}" आहे, जवळपासची शहरे जोडा`);
    }
    setSuggestions(tips);
  };

  const handleFieldChange = (field, value) => {
    setPreferences(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/profiles/preferences', preferences);
      toast.success('जोडीदार अपेक्षा यशस्वीरित्या सेव्ह केल्या!');
    } catch (error) {
      console.error('Failed to save preferences:', error);
      toast.error('सेव्ह करण्यात अयशस्वी. कृपया पुन्हा प्रयत्न करा.');
    } finally {
      setSaving(false);
    }
  };

  const religions = ['हिंदू', 'बौद्ध', 'जैन', 'ख्रिश्चन', 'मुस्लिम', 'शीख', 'इतर'];
  const qualifications = ['10th', '12th', 'Diploma', 'ITI', 'Graduate', 'Post Graduate', 'PhD', 'CA', 'CS', 'MBBS', 'B.E./B.Tech', 'MBA', 'Other'];
  const occupations = ['नोकरी', 'व्यवसाय', 'डॉक्टर', 'इंजिनिअर', 'शिक्षक', 'वकील', 'शासकीय नोकरी', 'IT प्रोफेशनल', 'शेती', 'गृहिणी', 'इतर'];
  const cities = ['पुणे', 'मुंबई', 'नाशिक', 'नागपूर', 'औरंगाबाद', 'कोल्हापूर', 'सोलापूर', 'सातारा', 'सांगली', 'रत्नागिरी', 'ठाणे', 'नवी मुंबई', 'इतर'];
  const states = ['Maharashtra', 'Karnataka', 'Goa', 'Madhya Pradesh', 'Gujarat', 'Delhi', 'Other'];
  const maritalStatuses = ['अविवाहित', 'विधवा/विधुर', 'घटस्फोटित', 'विभक्त', 'कोणतेही'];
  const motherTongues = ['मराठी', 'हिंदी', 'इंग्रजी', 'कन्नड', 'गुजराती', 'तेलुगू', 'इतर'];
  const complexions = ['गोरा', 'गहू वर्ण', 'सावळा', 'कोणतेही'];
  const castes = ['मराठा', 'ब्राह्मण', 'CKP', 'माळी', 'धनगर', 'कुणबी', 'लिंगायत', 'वाणी', 'सोनार', 'लोहार', 'सुतार', 'चांभार', 'महार', 'मातंग', 'बौद्ध', 'इतर'];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF8F0]">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-12 animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-64" />
          <div className="h-64 bg-gray-200 rounded-xl" />
          <div className="h-64 bg-gray-200 rounded-xl" />
          <div className="h-64 bg-gray-200 rounded-xl" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF8F0] flex flex-col">
      <Navbar />

      {/* Header */}
      <div className="bg-gradient-to-r from-[#8B0000] to-[#DC143C] py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#FFD700]/20 rounded-full flex items-center justify-center">
              <FaHeart className="text-[#FFD700] text-xl" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white font-['Noto_Sans_Devanagari']">
                जोडीदार अपेक्षा
              </h1>
              <p className="text-[#FFD700]/80 text-sm font-['Playfair_Display']">Partner Preferences</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex-1">
        {/* Smart Suggestions */}
        {suggestions.length > 0 && (
          <div className="bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <FaLightbulb className="text-[#B8860B]" />
              <h4 className="font-semibold text-[#8B0000] text-sm font-['Noto_Sans_Devanagari']">स्मार्ट सूचना</h4>
            </div>
            <ul className="space-y-1">
              {suggestions.map((tip, idx) => (
                <li key={idx} className="text-sm text-gray-600 font-['Noto_Sans_Devanagari'] flex items-start gap-2">
                  <span className="text-[#B8860B] mt-1">•</span> {tip}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="space-y-6">
          {/* Age & Physical */}
          <Section icon={FaUser} title="Basic Preferences / मूलभूत अपेक्षा">
            <div className="space-y-5">
              <RangeInput
                label="वय (वर्षे)"
                minValue={preferences.ageMin}
                maxValue={preferences.ageMax}
                onMinChange={v => handleFieldChange('ageMin', v)}
                onMaxChange={v => handleFieldChange('ageMax', v)}
                minPlaceholder="18"
                maxPlaceholder="45"
                type="number"
              />
              <RangeInput
                label="उंची"
                minValue={preferences.heightMin}
                maxValue={preferences.heightMax}
                onMinChange={v => handleFieldChange('heightMin', v)}
                onMaxChange={v => handleFieldChange('heightMax', v)}
                minPlaceholder="4'5&quot;"
                maxPlaceholder="6'5&quot;"
              />
              <CheckboxGroup
                label="वर्ण"
                options={complexions}
                selected={preferences.complexion}
                onChange={v => handleFieldChange('complexion', v)}
              />
              <SelectField
                label="वैवाहिक स्थिती"
                value={preferences.maritalStatus}
                options={maritalStatuses}
                onChange={v => handleFieldChange('maritalStatus', v)}
              />
            </div>
          </Section>

          {/* Community */}
          <Section icon={FaPray} title="Community / समाज">
            <div className="space-y-5">
              <CheckboxGroup
                label="धर्म"
                options={religions}
                selected={preferences.religion}
                onChange={v => handleFieldChange('religion', v)}
              />
              <CheckboxGroup
                label="जात (एक किंवा अनेक निवडा)"
                options={castes}
                selected={preferences.caste}
                onChange={v => handleFieldChange('caste', v)}
              />
              <CheckboxGroup
                label="मातृभाषा"
                options={motherTongues}
                selected={preferences.motherTongue}
                onChange={v => handleFieldChange('motherTongue', v)}
              />
            </div>
          </Section>

          {/* Education & Career */}
          <Section icon={FaGraduationCap} title="Education & Career / शिक्षण व करिअर">
            <div className="space-y-5">
              <CheckboxGroup
                label="शिक्षण (एक किंवा अनेक निवडा)"
                options={qualifications}
                selected={preferences.qualification}
                onChange={v => handleFieldChange('qualification', v)}
              />
              <CheckboxGroup
                label="व्यवसाय"
                options={occupations}
                selected={preferences.occupation}
                onChange={v => handleFieldChange('occupation', v)}
              />
              <RangeInput
                label="वार्षिक उत्पन्न (लाखात)"
                minValue={preferences.incomeMin}
                maxValue={preferences.incomeMax}
                onMinChange={v => handleFieldChange('incomeMin', v)}
                onMaxChange={v => handleFieldChange('incomeMax', v)}
                minPlaceholder="1"
                maxPlaceholder="50+"
                type="number"
              />
            </div>
          </Section>

          {/* Location */}
          <Section icon={FaMapMarkerAlt} title="Location / स्थान">
            <div className="space-y-5">
              <CheckboxGroup
                label="शहर (एक किंवा अनेक निवडा)"
                options={cities}
                selected={preferences.city}
                onChange={v => handleFieldChange('city', v)}
              />
              <CheckboxGroup
                label="राज्य"
                options={states}
                selected={preferences.state}
                onChange={v => handleFieldChange('state', v)}
              />
            </div>
          </Section>

          {/* Lifestyle */}
          <Section icon={FaUser} title="Lifestyle / जीवनशैली">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <SelectField
                label="आहार"
                value={preferences.diet}
                options={['शाकाहारी', 'मांसाहारी', 'एगिटेरियन', 'कोणतेही']}
                onChange={v => handleFieldChange('diet', v)}
              />
              <SelectField
                label="धूम्रपान"
                value={preferences.smoking}
                options={['नाही', 'कधीकधी', 'हो', 'कोणतेही']}
                onChange={v => handleFieldChange('smoking', v)}
              />
              <SelectField
                label="मद्यपान"
                value={preferences.drinking}
                options={['नाही', 'कधीकधी', 'हो', 'कोणतेही']}
                onChange={v => handleFieldChange('drinking', v)}
              />
            </div>
          </Section>

          {/* Save Button */}
          <div className="sticky bottom-4 z-10">
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-gradient-to-r from-[#8B0000] to-[#DC143C] text-white py-4 rounded-xl font-bold text-base hover:shadow-xl transition-all disabled:opacity-50 font-['Noto_Sans_Devanagari'] flex items-center justify-center gap-2 shadow-lg"
            >
              {saving ? (
                <>
                  <FaSpinner className="animate-spin" /> सेव्ह करत आहे...
                </>
              ) : (
                <>
                  <FaSave /> अपेक्षा सेव्ह करा
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PartnerPreferencePage;
